"use client";

import { motion } from "motion/react";
import { useState, ReactNode } from "react";
import { cn } from "@/lib/utils";

type Tab = { label: string; value: string; icon?: ReactNode; content: ReactNode };

const ACCENTS = ["red", "coral", "purple", "cyan", "denim", "emerald", "lime", "amber-gold", "crimson"] as const;
const ACCENT_TEXT: Record<string, string> = {
  red: "text-red-400 border-red-500",
  coral: "text-coral-400 border-coral-500",
  purple: "text-purple-400 border-purple-500",
  cyan: "text-cyan-400 border-cyan-500",
  denim: "text-denim-400 border-denim-500",
  emerald: "text-emerald-400 border-emerald-500",
  lime: "text-lime-400 border-lime-500",
  "amber-gold": "text-amber-gold-400 border-amber-gold-500",
  crimson: "text-crimson-400 border-crimson-500",
};

export function TabsBlock({ tabs, defaultValue, title, eyebrow, description }: { tabs: Tab[]; defaultValue?: string; title?: string; eyebrow?: string; description?: string }) {
  const [active, setActive] = useState(defaultValue ?? tabs[0]?.value);
  const activeIdx = tabs.findIndex((t) => t.value === active);
  const accent = ACCENTS[activeIdx % ACCENTS.length];

  return (
    <div className="space-y-8">
      {(eyebrow || title || description) && (
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mx-auto max-w-3xl text-center">
          {eyebrow && <div className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-amber-gold-500">{eyebrow}</div>}
          {title && <h2 className="font-rajdhani text-3xl font-bold text-foreground lg:text-4xl">{title}</h2>}
          {description && <p className="mt-3 text-foreground/80/80">{description}</p>}
        </motion.div>
      )}
      <div className="flex flex-wrap justify-center gap-2 border-b border-border">
        {tabs.map((tab) => {
          const isActive = active === tab.value;
          return (
            <button key={tab.value} onClick={() => setActive(tab.value)} className={cn("relative flex items-center gap-2 border-b-2 px-4 py-3 font-rajdhani text-sm font-semibold uppercase tracking-wider transition-colors", isActive ? cn(ACCENT_TEXT[accent]) : "border-transparent text-muted-foreground hover:text-foreground/85")}>
              {tab.icon}{tab.label}
            </button>
          );
        })}
      </div>
      <motion.div key={active} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="glass-card rounded-2xl border border-border p-8 lg:p-10">
        {tabs[activeIdx]?.content}
      </motion.div>
    </div>
  );
}
